import { BoxWrapper, ConvertStringToReact, CustomDropdown, ErrorMessage, Tooltip } from '@common/components'
import React, { FunctionComponent, useEffect, useState } from 'react'
import Pencil from '@common/images/icon-modify-on.svg'
import { first, get, isEmpty, range } from 'lodash-es'
import { BasePlanSku, SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { remapPricingObject } from '@buy/utils/volume-components-helpers'
import {
    createOrUpdateCheckoutSessionState,
    getCheckoutSessionState,
    setCheckoutSessionState,
    SessionKeys,
} from '@buy/utils'
import { ParamTypes } from '@buy/Routes'
import { useParams } from 'react-router-dom'
import styles from '../../connectedComponents/Customize/customize.module.css'

export interface DropdownComponentProps extends SKUSelectorsResponse {
    closeLabel: string
    showAdditionalLines: boolean
    baseLine: OrderSummaryLine
    setBaseLine: (value: OrderSummaryLine) => void
    additionalLines: Array<OrderSummaryLine>
    setAdditionalLines: (value: Array<OrderSummaryLine>) => void
    currencyCode: string
    isYearlyBillingFrequency: boolean
    basePlanDefaultVolume: Array<BasePlanSku>
    onChange: (value: string) => void
    value: string
}

interface VolumeComponentDropdownType {
    value: string
    multiplier: number
}

export const DropdownComponent: FunctionComponent<DropdownComponentProps> = ({
    additionalInformation,
    description,
    title,
    unit,
    volumeSelector,
    closeLabel,
    showAdditionalLines,
    setBaseLine,
    setAdditionalLines,
    additionalLines,
    baseLine,
    pricingInfo,
    currencyCode,
    basePlanDefaultVolume,
    onChange,
    value,
    sku,
    planName,
    quantityMultiplier,
}) => {
    const { pathProduct, plan } = useParams<ParamTypes>()
    const [isDropdownVisible, setIsDropdownVisible] = useState(false)
    const [dropdownItems, setDropdownItems] = useState<Array<VolumeComponentDropdownType>>([])

    const calculateDropdownValues = (loadedSessionValue: boolean) => {
        if (!isEmpty(volumeSelector)) {
            const { quantityPerUnit, customListValues, minimum, maximum } = volumeSelector
            if (quantityPerUnit && minimum && maximum) {
                const newValues = range(minimum, maximum + 1, quantityPerUnit).map((item, index) => ({
                    value: item.toString(),
                    multiplier: index + 1,
                }))
                if (!loadedSessionValue) onChange(first(newValues)!.value)
                setDropdownItems([...newValues])
            }
            if (!isEmpty(customListValues)) {
                const splittedValues = customListValues?.split('|')
                const remappedValues = splittedValues
                    ? splittedValues.map((option) => {
                          const splittedOptions = option.split(',')
                          return { value: splittedOptions[0], multiplier: Number(splittedOptions[1]) }
                      })
                    : []

                if (!loadedSessionValue) onChange(first(remappedValues)!.value)
                setDropdownItems([...remappedValues])
            }
        }
    }

    const onChangeDropdown = (input: string) => {
        onChange(input)
        createOrUpdateCheckoutSessionState({ [`${sku}`]: input }, SessionKeys.volumeFields, pathProduct!, plan!)

        const { multiplier } = dropdownItems.find((item) => item.value === input)!
        if (showAdditionalLines) {
            if (input === first(dropdownItems)?.value) {
                // remove from additional items
                const filteredAdditionalLines = additionalLines.filter((item) => item.unit !== unit.unitName)
                setAdditionalLines([...filteredAdditionalLines])
                setCheckoutSessionState([...filteredAdditionalLines], SessionKeys.additonalLines, pathProduct!, plan!)
                return // exit
            }

            const hasExistingAdditionalLine = additionalLines.find((item) => item.unit === unit.unitName)
            if (hasExistingAdditionalLine) {
                const newAdditionalLines = additionalLines.map((item) => {
                    const newItem = { ...item }
                    const defaultVolumeSelectorValue = basePlanDefaultVolume.find(
                        (item2) => item2.unit.unitName === newItem.unit
                    )!
                    if (item.unit === unit.unitName) {
                        newItem.skuName = planName
                        newItem.title = unit.additionalUnits
                        newItem.unit = unit.unitName
                        newItem.quantity = Number(input) - defaultVolumeSelectorValue.quantity
                        newItem.multiplier = Number(multiplier) - 1
                    }
                    return newItem
                })
                setAdditionalLines(newAdditionalLines)
                setCheckoutSessionState(newAdditionalLines, SessionKeys.additonalLines, pathProduct!, plan!)
            } else {
                const defaultVolumeSelectorValue = basePlanDefaultVolume.find(
                    (item) => item.unit.unitName === unit.unitName
                )!
                const newPrices = remapPricingObject(pricingInfo, currencyCode).allPrices
                const tempAdditionalLines: OrderSummaryLine[] = [
                    ...additionalLines,
                    {
                        key: sku,
                        title: unit.additionalUnits,
                        unit: unit.unitName,
                        quantity: Number(input) - defaultVolumeSelectorValue.quantity,
                        multiplier: Number(multiplier) - 1,
                        allPrices: newPrices,
                        skuName: planName,
                        quantityMultiplier,
                    },
                ]
                setAdditionalLines(tempAdditionalLines)
                setCheckoutSessionState(tempAdditionalLines, SessionKeys.additonalLines, pathProduct!, plan!)
            }
        } else {
            const tempBaseLine: OrderSummaryLine = {
                ...baseLine,
                multiplier: Number(multiplier),
                quantity: Number(multiplier),
            }
            setBaseLine(tempBaseLine)
            setCheckoutSessionState(tempBaseLine, SessionKeys.baseLine, pathProduct!, plan!)
        }
    }

    useEffect(() => {
        const sessionState = getCheckoutSessionState(pathProduct!, plan!)
        const hasSessionValue =
            !isEmpty(sessionState) && !isEmpty(get(sessionState, `${SessionKeys.volumeFields}.${sku}`))
        if (hasSessionValue) onChange(sessionState!.volumeFields[sku].toString())
        if (!isEmpty(volumeSelector)) {
            calculateDropdownValues(hasSessionValue)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeSelector])

    return (
        <>
            <BoxWrapper data-qat="plan-agents-selection">
                <>
                    <div className={styles['flex-header']}>
                        <h3 className={styles.label}>
                            {title.replace('{0}', `${value}`)}
                            <Tooltip content={additionalInformation} contentClass="customize__addon-tooltip" />
                        </h3>

                        {/* <span className={styles['addon-header__sub-text']}>
                                        {state.currentAgent.shortDescription.replace('{quantity}', `${state.agents}`)}
                                    </span> */}
                        <button onClick={() => setIsDropdownVisible(!isDropdownVisible)}>
                            {!isDropdownVisible && <img src={Pencil} alt="pencil" />}
                            {!isDropdownVisible && unit.updateUnits}
                            {isDropdownVisible && closeLabel}
                        </button>
                    </div>

                    {volumeSelector && volumeSelector.maximum && Number(value) >= volumeSelector.maximum && (
                        <ErrorMessage warning errorMessage={volumeSelector.maximumReachedMessage} />
                    )}

                    {isDropdownVisible && (
                        <div className={styles.license}>
                            <CustomDropdown
                                name={unit.unitName}
                                onChange={(event) => {
                                    if ((event.target as HTMLElement).innerText) {
                                        onChangeDropdown((event.target as HTMLElement).innerText)
                                    }
                                }}
                                value={value}
                                modifier={styles['addon-selector']}
                                label={<ConvertStringToReact htmlString={description} />}
                                labelClass={styles['addon-selector-label']}
                                additionalClass={styles['addon-selector-select']}
                                options={dropdownItems.map((item) => item.value)}
                                optionsContainerClass={styles['addon-selector-options-container']}
                                optionsClass={styles['addon-selector-option']}
                            />
                        </div>
                    )}
                </>
            </BoxWrapper>
        </>
    )
}
